import React, { ButtonHTMLAttributes, FC, useMemo } from "react";
import { Button, Spinner, UncontrolledTooltip } from "reactstrap";
import classNames from "classnames";
import { Message } from "shared/components/Message";
import { Money } from "components/shared/Money";
import { paymentMessages } from "domain/payment/paymentMessages";

interface IProps extends ButtonHTMLAttributes<HTMLButtonElement> {
    amount: number;
    currency: string;
    isDisabled: boolean;
    isProcessing: boolean;
    disabledReason?: string;
    className?: string;
    size?: string;
    onClick?: () => void;
}

export const BasePaymentButton: FC<IProps> = ({
    amount,
    currency,
    isDisabled,
    isProcessing,
    disabledReason,
    className = "w-50",
    size,
    onClick,
    id,
    ...rest
}) => {
    const buttonId = "payment-form-submit-btn";
    const wrapperId = `${buttonId}-wrapper`;
    const descriptionId = `${buttonId}-description`;
    const isActuallyDisabled = isDisabled || isProcessing;

    return (
        <div className="text-center">
            <span
                id={wrapperId}
                className={classNames("d-inline-block", className)}
                tabIndex={isActuallyDisabled ? 0 : -1}
                style={isActuallyDisabled ? { cursor: "not-allowed" } : undefined}
            >
                <Button
                    id={buttonId}
                    type="button"
                    className="w-100"
                    size={size}
                    color={!isActuallyDisabled ? "primary" : "secondary"}
                    onClick={onClick}
                    disabled={isActuallyDisabled}
                    aria-describedby={isActuallyDisabled && disabledReason ? descriptionId : undefined}
                    style={isActuallyDisabled ? { pointerEvents: "none" } : undefined}
                    {...rest}
                >
                    {isProcessing && (
                        <Spinner className="icon-button ml-2" aria-hidden="true" />
                    )}
                    <span className="ml-2">
                        {isProcessing ? (
                            <Message message={paymentMessages.paymentIsProcessing} />
                        ) : (
                            <>
                                <Message message={paymentMessages.payButtonLabel} />{" "}
                                <Money amount={amount} currency={currency} />
                            </>
                        )}
                    </span>
                </Button>
            </span>

            {isActuallyDisabled && disabledReason && (
                <UncontrolledTooltip target={wrapperId} placement="top" aria-hidden="true">
                    {disabledReason}
                </UncontrolledTooltip>
            )}

            {isActuallyDisabled && disabledReason && (
                <span id={descriptionId} className="sr-only">
                    {disabledReason}
                </span>
            )}
        </div>
    );
};
